import React, { useEffect, useRef, useState } from "react";
import Tag from "./Tag";
import { day, money, num } from "../format";

/** Stock (line) and units shipped (bars) per day, from the inventory ledger. */
function StockChart({ points }) {
  if (!points || points.length < 2) {
    return <p className="ia-muted">The chart fills in as daily ledger reports arrive.</p>;
  }
  const W = 360;
  const H = 120;
  const maxStock = Math.max(...points.map((p) => p.endingBalance), 1);
  const maxSold = Math.max(...points.map((p) => p.shipped), 1);
  const x = (i) => (i / (points.length - 1)) * (W - 8) + 4;
  const line = points.map((p, i) => `${x(i)},${H - 16 - (p.endingBalance / maxStock) * (H - 28)}`).join(" ");
  const barW = Math.max(2, (W - 8) / points.length - 2);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="ia-chart" role="img" aria-label="Stock and units shipped per day">
      {points.map((p, i) => {
        const h = (p.shipped / maxSold) * 30;
        return <rect key={p.date} x={x(i) - barW / 2} y={H - 14 - h} width={barW} height={h} className="ia-chart__bar" />;
      })}
      <polyline points={line} className="ia-chart__line" />
      <text x="4" y={H - 2} className="ia-chart__label">{day(points[0].date)}</text>
      <text x={W - 4} y={H - 2} textAnchor="end" className="ia-chart__label">{day(points[points.length - 1].date)}</text>
      <text x="4" y="10" className="ia-chart__label">Stock {num(points[points.length - 1].endingBalance)}</text>
    </svg>
  );
}

const AGE_LABELS = { "0-90": "0–90 days", "91-180": "91–180", "181-270": "181–270", "271-365": "271–365", "365+": "365+" };

export default function SkuDrawer({ sku, loadSku, onClose, currency }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setDetail(null);
    setError(null);
    loadSku(sku)
      .then((d) => alive && (d && d.error ? setError(d.error) : setDetail(d)))
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [sku, loadSku]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const n = detail && detail.numbers;
  return (
    <>
      <div className="ia-scrim" onClick={onClose} aria-hidden="true" />
      <aside className="ia-drawer" role="dialog" aria-modal="true" aria-label={`Details for ${sku}`}>
        <header className="ia-drawer__head">
          <div>
            <h2 className="ia-drawer__sku">{sku}</h2>
            <div className="ia-muted">{detail ? detail.name : ""}</div>
          </div>
          <div className="ia-drawer__headright">
            {detail && detail.alert ? <Tag level={detail.alert.level}>{detail.alert.label}</Tag> : null}
            <button type="button" className="ia-btn" onClick={onClose} ref={closeRef}>
              Close
            </button>
          </div>
        </header>

        {error ? <div className="ia-alert">{error}</div> : null}
        {!detail && !error ? <div className="ia-muted ia-pad">Loading…</div> : null}

        {detail ? (
          <div className="ia-drawer__body">
            <div className="ia-numbers">
              <div><span>FBA available</span><b>{num(n.fbaAvailable)}</b></div>
              <div><span>Inbound to FBA</span><b>{num(n.inboundToFba)}</b></div>
              <div><span>In AWD</span><b>{num(n.inAwd)}</b></div>
              <div><span>Days of cover</span><b className={detail.alert && detail.alert.level === "crit" ? "ia-text--crit" : ""}>{n.coverDays === null ? "—" : Math.round(n.coverDays)}</b></div>
              <div><span>Needs (lead + safety)</span><b>{n.needsDays}</b></div>
              <div><span>Avg daily sales (30 d)</span><b>{n.avgDailySales30d}</b></div>
              <div><span>Reserved</span><b>{num(n.reserved)}</b></div>
              <div><span>Unsellable</span><b>{num(n.unsellable)}</b></div>
              <div><span>Storage fee, last month</span><b>{money(n.storageFeeLastMonth, currency)}</b></div>
            </div>

            {detail.recommendation && detail.recommendation.action && detail.recommendation.action !== "—" ? (
              <div className={`ia-rec${detail.alert && detail.alert.level === "crit" ? " ia-rec--urgent" : ""}`}>
                <div className="ia-rec__label">{detail.recommendation.label}</div>
                <div className="ia-rec__action">{detail.recommendation.action}</div>
                <div className="ia-muted">{detail.recommendation.why}</div>
                {detail.recommendation.amazon && detail.recommendation.amazon.recommendedQty ? (
                  <div className="ia-muted">
                    Amazon suggests {num(detail.recommendation.amazon.recommendedQty)} units
                    {detail.recommendation.amazon.shipDate ? ` by ${day(detail.recommendation.amazon.shipDate)}` : ""}.
                  </div>
                ) : null}
              </div>
            ) : null}

            {detail.stranded ? (
              <div className="ia-rec ia-rec--urgent">
                <div className="ia-rec__label">Stranded</div>
                <div className="ia-rec__action">{num(detail.stranded.qty)} units: {detail.stranded.reason}</div>
                {detail.stranded.autoRemoval ? <div className="ia-muted">Auto-removal on {day(detail.stranded.autoRemoval)}</div> : null}
              </div>
            ) : null}

            <h3 className="ia-subtitle">Stock vs. sales, last 60 days</h3>
            <StockChart points={detail.stockVsSales60d} />

            <h3 className="ia-subtitle">Inventory age</h3>
            <div className="ia-agebar" aria-label="Inventory age">
              {Object.entries(detail.inventoryAge).map(([k, v]) =>
                v.pct ? <i key={k} className={`ia-age--${k.replace("+", "p")}`} style={{ width: `${v.pct}%` }} title={`${AGE_LABELS[k]}: ${v.units}`} /> : null
              )}
            </div>
            <div className="ia-muted ia-small">
              {Object.entries(detail.inventoryAge).map(([k, v]) => `${AGE_LABELS[k]} ${v.pct}%`).join(", ")}
            </div>

            {detail.shipments.length ? (
              <>
                <h3 className="ia-subtitle">Shipments</h3>
                <ul className="ia-plainlist">
                  {detail.shipments.map((s) => (
                    <li key={s.id}>
                      <b>{s.title}</b> {s.column}: <Tag level={s.tag.level}>{s.tag.label}</Tag>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {detail.claims.length ? (
              <>
                <h3 className="ia-subtitle">Claims</h3>
                <ul className="ia-plainlist">
                  {detail.claims.map((c) => (
                    <li key={c.id}>
                      <b>{c.title}</b>, {c.units} units, {money(c.value, currency)} <Tag level={c.stage.level}>{c.stage.label}</Tag>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : null}
      </aside>
    </>
  );
}
